/* eslint-disable react-refresh/only-export-components */
import { useEffect, type ReactNode } from "react";
import { ErrorComponent, type ErrorComponentProps, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import * as Sentry from "@sentry/react";
import { Button, ErrorBoundary, ErrorPage } from "@h2j-data-team/ui";

import { ApiError } from "@/lib/api-client";

/*
 * Error pages on the library's ErrorPage. The thrown error's message never
 * reaches the screen (it can be internal server text); on a 5xx the request ID
 * becomes a support code instead. In development TanStack's overlay is kept
 * below the page for fast debugging.
 */

export function supportCode(error: unknown, label: string) {
  return error instanceof ApiError && error.status >= 500 && error.requestId
    ? { label, value: error.requestId }
    : undefined;
}

function backToHome() {
  window.location.assign("/");
}

/**
 * Router `defaultErrorComponent`: errors thrown in a route's beforeLoad, loader
 * or component. Those don't pass through a React boundary's onError, so it
 * reports to Sentry itself. "Try again" resets the boundary and re-runs loaders.
 */
export function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const { t } = useTranslation("common");

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <ErrorPage
      title={t("somethingWentWrong")}
      description={
        <>
          {t("pageError")}
          {import.meta.env.DEV && (
            <div className="mt-4 text-left">
              <ErrorComponent error={error} />
            </div>
          )}
        </>
      }
      supportCode={supportCode(error, t("error.supportCode"))}
      actions={
        <>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              void router.invalidate();
            }}
          >
            <RefreshCw />
            {t("tryAgain")}
          </Button>
          <Button onClick={backToHome}>{t("error.backToHome")}</Button>
        </>
      }
    />
  );
}

/** Router `defaultNotFoundComponent`: the 404 for unknown URLs. */
export function NotFoundScreen() {
  const { t } = useTranslation("common");
  return (
    <ErrorPage
      kind="not-found"
      code="404"
      title={t("error.pageNotFound")}
      description={t("error.pageNotFoundMessage")}
      actions={<Button onClick={backToHome}>{t("error.backToHome")}</Button>}
    />
  );
}

/** The fallback of AppErrorBoundary: the crashed tree cannot re-render in place, so recovery is a reload. */
function AppErrorFallback({ error }: { error: unknown }) {
  const { t } = useTranslation("common");
  return (
    <ErrorPage
      title={t("error.appCrashedTitle")}
      description={t("error.appCrashedMessage")}
      supportCode={supportCode(error, t("error.supportCode"))}
      actions={
        <>
          <Button variant="outline" onClick={() => window.location.reload()}>
            <RefreshCw />
            {t("error.reloadPage")}
          </Button>
          <Button onClick={backToHome}>{t("error.backToHome")}</Button>
        </>
      }
    />
  );
}

/**
 * Top-level boundary: the last line of defense for render errors that escape
 * the router's per-route boundaries (providers, the router root). Reports to
 * Sentry with the component stack.
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={({ error }) => <AppErrorFallback error={error} />}
      onError={(error, info) =>
        Sentry.captureException(error, {
          contexts: { react: { componentStack: info.componentStack } },
        })
      }
    >
      {children}
    </ErrorBoundary>
  );
}
